/**
 * Pure view model for the pool settings Chat card (ISSUE-1040).
 *
 * This module is pure: no React import, no DOM, no fetch, no SDK. The switch's
 * source of truth is `/pool/get`'s `pool.chatEnabled`, written through
 * `/pool/update { chatEnabled }`. A membership change resends
 * `pools.chatEnabled` to messaging-api, so the chat row is not a second
 * switch. `Who can post` reads `postPolicy` from
 * `/chat/get` and writes `/chat/set-post-policy`.
 *
 * The accepted values are a bridge, not a second source of truth: they cover
 * the gap between a successful write and the refetch its SDK hook triggers
 * (debounced by 150ms), and `settleAcceptedValue` hands control back to the
 * server read once that read shows the accepted value. Known window, accepted
 * on purpose: if another Admin flips the value back before this card's own
 * post-write refetch lands, the server read never equals the accepted value,
 * so this card keeps the value it saved until it remounts. That needs two
 * Admins writing the same switch within a fraction of a second.
 */

export const CHAT_SETTINGS_SWITCH_LABEL = "Chat";
export const CHAT_SETTINGS_SWITCH_DESCRIPTION =
  "Turning it off locks the chat. Members can still read it, and nothing is deleted.";
export const CHAT_SETTINGS_POST_POLICY_LABEL = "Who can post";
export const CHAT_SETTINGS_POST_POLICY_EVERYONE_LABEL = "Everyone";
export const CHAT_SETTINGS_POST_POLICY_ANNOUNCEMENTS_LABEL =
  "Announcements only";
export const CHAT_SETTINGS_POST_POLICY_HELP =
  "In announcements only, members can still react.";
export const CHAT_SETTINGS_POST_POLICY_OFF_REASON =
  "Turn chat on to choose who can post.";
export const CHAT_SETTINGS_POST_POLICY_SETUP_REASON =
  "Available once the chat is set up.";
export const CHAT_SETTINGS_POST_POLICY_LOADING_REASON = "Loading who can post.";
export const CHAT_SETTINGS_POST_POLICY_FAILED_REASON =
  "We could not load who can post.";

export type typeChatPostPolicy = "everyone" | "officersOnly";

export type typeChatSettingsCardInput = {
  /** `/pool/get` data.pool.chatEnabled; undefined while that read has not answered. */
  poolChatEnabled: boolean | undefined;
  /** The value the switch was just flipped to, while its /pool/update is in flight. */
  chatEnabledInFlight: boolean | null;
  /** chatEnabled from the last successful /pool/update on this mount, else null. */
  chatEnabledAccepted: boolean | null;
  /** True once /chat/list-mine has answered a list (items !== null). */
  isChatListLoaded: boolean;
  /** True when /chat/list-mine failed (transport error or an error envelope). */
  isChatListFailed: boolean;
  /** This pool's chat id from findPoolChat, or null when the loaded list has none. */
  chatId: string | null;
  /** `/chat/get` data.postPolicy, or null while not answered or not requested. */
  serverPostPolicy: typeChatPostPolicy | null;
  /** True when /chat/get failed (transport error or an error envelope). */
  isPostPolicyFailed: boolean;
  /** The policy just chosen, while its /chat/set-post-policy is in flight. */
  postPolicyInFlight: typeChatPostPolicy | null;
  /** postPolicy from the last successful /chat/set-post-policy on this mount, else null. */
  postPolicyAccepted: typeChatPostPolicy | null;
};

export type typeChatSettingsCardModel = {
  chatSwitch: { checked: boolean; disabled: boolean };
  postPolicy: {
    value: typeChatPostPolicy | null;
    disabled: boolean;
    /** One line under the control saying why it is disabled, or null. */
    disabledReason: string | null;
  };
};

export const CHAT_SETTINGS_POST_POLICY_ITEMS: readonly {
  value: typeChatPostPolicy;
  label: string;
}[] = [
  { value: "everyone", label: CHAT_SETTINGS_POST_POLICY_EVERYONE_LABEL },
  {
    value: "officersOnly",
    label: CHAT_SETTINGS_POST_POLICY_ANNOUNCEMENTS_LABEL,
  },
];

function resolvePostPolicyGate(
  input: typeChatSettingsCardInput,
  chatEnabled: boolean | null,
  value: typeChatPostPolicy | null,
): { disabled: boolean; disabledReason: string | null } {
  if (chatEnabled !== true) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_OFF_REASON,
    };
  }
  if (input.isChatListFailed) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_FAILED_REASON,
    };
  }
  if (!input.isChatListLoaded) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_LOADING_REASON,
    };
  }
  if (input.chatId === null) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_SETUP_REASON,
    };
  }
  if (input.isPostPolicyFailed) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_FAILED_REASON,
    };
  }
  if (value === null) {
    return {
      disabled: true,
      disabledReason: CHAT_SETTINGS_POST_POLICY_LOADING_REASON,
    };
  }
  if (input.postPolicyInFlight !== null) {
    return { disabled: true, disabledReason: null };
  }
  return { disabled: false, disabledReason: null };
}

export function resolveChatSettingsCard(
  input: typeChatSettingsCardInput,
): typeChatSettingsCardModel {
  const chatEnabled =
    input.chatEnabledInFlight ??
    input.chatEnabledAccepted ??
    input.poolChatEnabled ??
    null;
  const value =
    input.postPolicyInFlight ??
    input.postPolicyAccepted ??
    input.serverPostPolicy;
  return {
    chatSwitch: {
      checked: chatEnabled === true,
      disabled:
        input.poolChatEnabled === undefined ||
        input.chatEnabledInFlight !== null,
    },
    postPolicy: { value, ...resolvePostPolicyGate(input, chatEnabled, value) },
  };
}

export function readPostPolicyValue(value: string): typeChatPostPolicy | null {
  if (value === "everyone") return "everyone";
  if (value === "officersOnly") return "officersOnly";
  return null;
}

/**
 * Drops an accepted value once the server read shows it (the server has
 * caught up, so control goes back to it); keeps it until then.
 */
export function settleAcceptedValue<T extends boolean | string>(input: {
  accepted: T | null;
  server: T | null | undefined;
}): T | null {
  if (input.accepted !== null && input.server === input.accepted) {
    return null;
  }
  return input.accepted;
}
